import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Avatar, Box, Chip, List, ListItem, ListItemAvatar, 
  ListItemButton, ListItemText, Paper, Stack, Typography, Fade
} from '@mui/material';
import React from 'react';
import { useAgendamentoStore } from '../../store/useAgendamentoStore';
import { ProfissionalDto } from '../../agendamento.types';
import { InfoAgendamento } from '../agendamento/InfoAgendamento';

export const StepProfissional: React.FC = () => {
  const { profissionaisDisponiveis, data, horario, setProfissional, setHorario } = useAgendamentoStore();

  // Ordenação alfabética dos profissionais
  const profissionaisOrdenados = [...profissionaisDisponiveis].sort((a, b) => 
    a.nome.localeCompare(b.nome)
  );

  const profissionaisNoDia = profissionaisOrdenados.filter(p => {
    const horas = p.diaHorarios[data!] || [];
    return horas.length > 0;
  });

  const disponiveisAgora = profissionaisNoDia.filter(p => 
    p.diaHorarios[data!]?.some((h: string) => h.startsWith(horario!))
  );

  const outrosProfissionais = profissionaisNoDia.filter(p => 
    !disponiveisAgora.find(d => d.codigo === p.codigo)
  );

  const renderProfissional = (p: ProfissionalDto, isDestaque: boolean) => {
    const horariosDoDia = p.diaHorarios[data!] || [];
    const fotoUrl = p.foto?.startsWith('data:image') 
      ? p.foto 
      : p.foto ? `data:image/png;base64,${p.foto.replace(/\s/g, '')}` : undefined;

    return (
      <ListItem 
        key={p.codigo} 
        disablePadding 
        divider 
        sx={{ 
          flexDirection: 'column', 
          alignItems: 'stretch',
          bgcolor: isDestaque ? 'transparent' : 'rgba(255,255,255,0.01)',
          borderColor: 'rgba(255,255,255,0.05)'
        }}
      >
        <ListItemButton 
          onClick={() => setProfissional(p)} 
          sx={{ py: 3, px: 2, alignItems: 'center' }}
        >
          <ListItemAvatar sx={{ mr: 2.5 }}>
            <Avatar 
              src={fotoUrl} 
              sx={{ 
                width: 65, height: 65,
                border: isDestaque ? '3px solid var(--color-primary)' : '2px solid rgba(255,255,255,0.1)',
                boxShadow: isDestaque ? '0 0 15px rgba(var(--color-primary-rgb), 0.3)' : 'none'
              }}
            >
              {p.nome.charAt(0)}
            </Avatar>
          </ListItemAvatar>
          
          <ListItemText 
            primary={
              <Box display="flex" flexDirection="column" gap={0.5} mb={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={900} variant="h6" sx={{ color: 'var(--color-text)', lineHeight: 1.2 }}>
                    {p.nome}
                  </Typography>
                  {isDestaque && (
                    <Chip 
                      label="Disponível agora" 
                      size="small" 
                      sx={{ 
                        bgcolor: 'var(--color-primary)', 
                        color: 'var(--color-background)', // Contraste dinâmico
                        fontWeight: 900, 
                        height: 18, 
                        fontSize: '9px',
                        textTransform: 'uppercase'
                      }} 
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
                  {p.apresentacao || 'Especialista'}
                </Typography>
              </Box>
            }
          />

          <Box textAlign="right" sx={{ ml: 2 }}>
            <Typography variant="body1" fontWeight={900} color="var(--color-primary)">
              + {p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </Typography>
          </Box>
        </ListItemButton>

        <Box sx={{ px: { xs: 2, sm: 12 }, pb: 3 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, opacity: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 14 }} /> HORÁRIOS DISPONÍVEIS:
          </Typography>
          
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {horariosDoDia.map((h: string) => {
              const hFormat = h.substring(0, 5);
              const isSelected = hFormat === horario;

              return (
                <Chip
                  key={h}
                  label={hFormat}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); 
                    setHorario(data!, hFormat);
                    setProfissional(p);
                  }}
                  sx={{ 
                    cursor: 'pointer', borderRadius: '6px', 
                    fontWeight: isSelected ? 900 : 600,
                    bgcolor: isSelected ? 'var(--color-primary)' : 'rgba(255,255,255,0.05)',
                    color: isSelected ? 'var(--color-background)' : 'var(--color-text)', // Contraste dinâmico
                    border: '1px solid',
                    borderColor: isSelected ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                    transition: '0.2s',
                    '&:hover': {
                      bgcolor: isSelected ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      </ListItem>
    );
  };

  return (
    <Fade in timeout={500}>
      <Box>
        <InfoAgendamento />
        
        <Typography variant="h6" fontWeight={900} mb={3} sx={{ color: 'var(--color-text)', pl: 1 }}>
          Escolha o Profissional
        </Typography>

        <Paper elevation={0} sx={{ 
          borderRadius: 6, 
          overflow: 'hidden', 
          border: '1px solid rgba(255,255,255,0.08)', 
          bgcolor: 'var(--color-surface)' 
        }}>
          <List disablePadding>
            {disponiveisAgora.map(p => renderProfissional(p, true))}

            {outrosProfissionais.length > 0 && (
              <>
                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderY: '1px solid rgba(255,255,255,0.05)' }}>
                  <Typography variant="caption" fontWeight={900} color="text.secondary" sx={{ letterSpacing: 1.5, opacity: 0.5 }}>
                    OUTRAS OPÇÕES PARA ESTE DIA
                  </Typography>
                </Box>
                {outrosProfissionais.map(p => renderProfissional(p, false))}
              </>
            )}
          </List>
        </Paper>
      </Box>
    </Fade>
  );
};